import { Typography } from 'src/components/common/Typography'
import {
  Noto_body1_b16,
  Noto_body2_b14,
  Noto_body2_m14,
  Noto_body2_r14,
  Noto_heading3_b20,
} from 'src/styles/fonts/NotoSansKrFonts'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { Container } from 'src/components/common/Container'
import CouponCircleImage from 'src/components/common/CouponCircleImage'
import Skeleton from 'react-loading-skeleton'
import { getBrandAndCouponName } from 'src/utils'
import { ellip } from 'src/styles/mixin'
import { Roboto_num3_r14 } from 'src/styles/fonts/RobotoFonts'
import { GiftReceivePageProps } from 'src/pages/GiftCardPage/GiftReceivePage'

const textLogo = `${process.env.PUBLIC_URL}/images/text_logo.svg`

const GiftReceiveMsgCard = ({ giftDetailData }: GiftReceivePageProps) => {
  const imgRef = useRef<HTMLImageElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [bgColor, setBgColor] = useState<string>('')
  const { PNumber, CouponImage, CouponName, BrandName, Msg, CardImg } = giftDetailData

  const handleLoad = () => {
    const img = imgRef.current
    const canvas = canvasRef.current
    if (!img || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    // 캔버스에 그림
    ctx.drawImage(img, 0, 0)

    try {
      const imageData = ctx.getImageData(3, 3, 1, 1).data
      const [r, g, b, a] = Array.from(imageData)
      const rgba = `rgba(${r}, ${g}, ${b}, ${a / 255})`
      setBgColor(rgba)
    } catch (error) {
      console.warn('색상 추출 실패:', error)
      setBgColor('')
    }
  }

  return (
    <S.GiftReceiveMsgCardContainer $option="flex_row" $bgColor={bgColor}>
      <S.GiftMsgCardContentsContainer $option="flex_column">
        <S.GiftMsgCardTopContainer $option="flex_column">
          <Typography $fontStyle={Noto_heading3_b20} $color="var(--black-90)">
            {PNumber?.slice(-4)}님이
            <br />
            쿠폰 선물을 보냈어요.
          </Typography>
          <S.MsgCardImage
            src={CardImg}
            alt="메시지 카드"
            ref={imgRef}
            onLoad={handleLoad}
            crossOrigin="anonymous"
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </S.GiftMsgCardTopContainer>
        <S.GiftMsgCardMiddleContainer $option="flex_column" $row_gap="24px">
          <S.CouponInfoContainer $option="flex_column">
            <CouponCircleImage src={CouponImage} />
            <S.CouponInfoBrandName as={!!CouponName ? 'span' : Skeleton}>
              [{BrandName}]
            </S.CouponInfoBrandName>
            <S.CouponInfoCouponName as={!!CouponName ? 'span' : Skeleton}>
              {getBrandAndCouponName(CouponName).couponName}
            </S.CouponInfoCouponName>
          </S.CouponInfoContainer>
          <S.CouponNotiContainer>
            • 선물 받기를 누르면 교환권 바코드가 발급됩니다.
            <br />• 기간 내에 선물을 받지 않으면 선물이 자동 거절되니 유의해주세요.
            <br />• 선물 거절시, 선물을 보낸 분께 알림이 발송됩니다.
          </S.CouponNotiContainer>
          <S.TextLogoImage src={textLogo} alt="logo" />
        </S.GiftMsgCardMiddleContainer>
        <S.MsgDivider $option="flex_row" $column_gap="10px">
          <S.MsgDividerLine />
          Message
          <S.MsgDividerLine />
        </S.MsgDivider>
        <S.GiftMsgCardBottomContainer $option="flex_column" $row_gap="4px">
          <Typography $fontStyle={Noto_body2_b14} $color="#303030">
            메시지
          </Typography>
          <S.MsgBox $fontStyle={Noto_body2_r14} $color="#303030">
            “ {Msg} ”
          </S.MsgBox>
        </S.GiftMsgCardBottomContainer>
      </S.GiftMsgCardContentsContainer>
    </S.GiftReceiveMsgCardContainer>
  )
}

export default GiftReceiveMsgCard

const S = {
  GiftReceiveMsgCardContainer: styled(Container)<{ $bgColor: string }>`
    justify-content: center;
    padding: 30px 20px;
    background-color: ${({ $bgColor }) => $bgColor};
  `,
  GiftMsgCardContentsContainer: styled(Container)`
    max-width: 320px;
    width: 100%;
  `,
  GiftMsgCardTopContainer: styled(Container)`
    align-items: center;
    text-align: center;
  `,
  MsgCardImage: styled.img`
    width: 230px;
    height: 230px;
    margin: 20px;
  `,
  GiftMsgCardMiddleContainer: styled(Container)`
    align-items: center;
    padding: 30px 20px;
    border-radius: 20px;
    background-color: var(--white);
  `,
  CouponInfoContainer: styled(Container)`
    align-items: center;
  `,
  CouponInfoBrandName: styled(Typography)`
    margin: 14px 0 2px 0;
    ${Noto_body2_m14};
    color: var(--black-50);
  `,
  CouponInfoCouponName: styled(Typography)`
    ${Noto_body1_b16};
    color: var(--black-90);
    ${ellip('line2')};
    text-align: center;
  `,
  CouponNotiContainer: styled(Container)`
    ${Noto_body2_r14};
    padding: 14px;
    border-radius: 12px;
    background-color: var(--black-4);
    color: var(--black-50);
  `,
  TextLogoImage: styled.img`
    width: 120px;
  `,
  MsgDivider: styled(Container)`
    ${Roboto_num3_r14};
    justify-content: center;
    align-items: center;
    margin: 14px 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.3);
  `,
  MsgDividerLine: styled(Container)`
    width: 101px;
    height: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.3);
  `,
  GiftMsgCardBottomContainer: styled(Container)`
    padding: 20px;
    border-radius: 26px 26px 26px 2px;
    background-color: rgba(255, 255, 255, 0.55);
  `,
  MsgBox: styled(Typography)`
    white-space: pre-wrap;
  `,
}
